/**
 * Formulaire d'une carte : type et mode (design system, ChoixDuTypeDeCarte), puis les champs
 * (Editeur) ou la préparation du masquage (EditeurDeMasquage). Partagé par la création, la
 * modification depuis la gestion des cartes et la modification pendant la séance.
 */
import { forwardRef, useCallback, useImperativeHandle, useRef } from 'react';
import {
  clesCartesFilles,
  differenceCartesFilles,
  MODES_PAR_DEFAUT,
  normaliserCarteMere,
  validerCarteMere,
  type CarteMere,
  type ContenuBasique,
  type ContenuImage,
  type ContenuTableau,
  type ContenuTrous,
  type ModeMasquage,
  type ModeTrous,
  type TypeCarte,
} from '../../cartes/carte-mere';
import { analyserTrous } from '../../cartes/trous';
import { trousAffiches } from '../../contenu/syntaxe';
import { CaseACocher } from '../composants/CaseACocher';
import { CommandeSegmentee } from '../composants/CommandeSegmentee';
import { Icone } from '../composants/Icone';
import { ListeDeroulante, type OptionListe } from '../composants/ListeDeroulante';
import { MessageDeChamp } from '../composants/MessageDeChamp';
import { pluriel } from '../outils';
import type { ResoudreImage } from './apercu';
import { Editeur, type EditeurPoignee, type NomChamp } from './Editeur';
import { EditeurDeTableau, EditeurDImage, tableauVide } from './EditeurDeMasquage';

/** Brouillon : le contenu de chaque type est gardé quand on change de type. */
export interface Brouillon {
  type: TypeCarte;
  modes: { [T in TypeCarte]: Extract<CarteMere, { type: T }>['mode'] };
  basique: ContenuBasique;
  trous: ContenuTrous;
  image: ContenuImage;
  tableau: ContenuTableau;
}

export function brouillonVide(
  type: TypeCarte,
  modes: Brouillon['modes'] = MODES_PAR_DEFAUT,
): Brouillon {
  return {
    type,
    modes: { ...modes },
    basique: { recto: '', verso: '' },
    trous: { texte: '' },
    image: { image: '', zones: [] },
    tableau: tableauVide(),
  };
}

export function brouillonDepuisCarte(
  carte: CarteMere,
  modes: Brouillon['modes'] = MODES_PAR_DEFAUT,
): Brouillon {
  const brouillon = brouillonVide(carte.type, modes);
  (brouillon.modes as Record<TypeCarte, string>)[carte.type] = carte.mode;
  switch (carte.type) {
    case 'basique':
      brouillon.basique = { ...carte.contenu };
      break;
    case 'trous':
      brouillon.trous = { ...carte.contenu };
      break;
    case 'image':
      brouillon.image = structuredClone(carte.contenu);
      break;
    case 'tableau':
      brouillon.tableau = structuredClone(carte.contenu);
      break;
  }
  return brouillon;
}

export function carteDuBrouillon(b: Brouillon): CarteMere {
  switch (b.type) {
    case 'basique':
      return { type: 'basique', mode: b.modes.basique, contenu: b.basique };
    case 'trous':
      return { type: 'trous', mode: b.modes.trous, contenu: b.trous };
    case 'image':
      return { type: 'image', mode: b.modes.image, contenu: b.image };
    case 'tableau':
      return { type: 'tableau', mode: b.modes.tableau, contenu: b.tableau };
  }
}

export type ErreursCarte = Partial<Record<NomChamp | 'masquage', string>>;

/** Erreurs de validation rangées par champ, avec les messages du design system. */
export function erreursDuBrouillon(b: Brouillon): ErreursCarte {
  const erreurs: ErreursCarte = {};
  for (const message of validerCarteMere(normaliserCarteMere(carteDuBrouillon(b)))) {
    if (message.startsWith('Le recto')) erreurs.recto = message;
    else if (message.startsWith('Le verso')) erreurs.verso = message;
    else if (message.includes('aucun trou'))
      erreurs.texte = 'Ajoutez au moins un trou : entourez la réponse de {{ et }}.';
    else if (message.includes('trou')) erreurs.texte = message;
    else if (message === 'Aucune image.') erreurs.masquage = 'Ajoutez une image.';
    else if (message.includes('zone de masquage'))
      erreurs.masquage = 'Dessinez au moins une zone sur l’image.';
    else if (message.includes('case grisée'))
      erreurs.masquage =
        'Grisez au moins une case : passez en « Griser les cases » et cliquez celles à masquer.';
    else erreurs.masquage = message;
  }
  if (b.type === 'trous' && erreurs.texte === undefined) {
    const saisis = analyserTrous(b.trous.texte).length;
    if (trousAffiches(b.trous.texte) < saisis) {
      erreurs.texte =
        'Un trou est pris dans une formule mal fermée : le $ qui ferme une formule ne doit pas suivre une espace.';
    }
  }
  return erreurs;
}

/** Nombre de cartes que génère le brouillon. */
export function cartesGenerees(b: Brouillon): number {
  return clesCartesFilles(normaliserCarteMere(carteDuBrouillon(b))).length;
}

const TYPES: {
  valeur: TypeCarte;
  libelle: string;
  icone: 'rectangle-horizontal' | 'square-dashed' | 'image' | 'table';
}[] = [
  { valeur: 'basique', libelle: 'Basique', icone: 'rectangle-horizontal' },
  { valeur: 'trous', libelle: 'Texte à trous', icone: 'square-dashed' },
  { valeur: 'image', libelle: 'Masquage d’image', icone: 'image' },
  { valeur: 'tableau', libelle: 'Masquage de tableau', icone: 'table' },
];

const MODES_TROUS: OptionListe<ModeTrous>[] = [
  {
    valeur: 'un_par_carte',
    libelle: 'Un trou par carte',
    description: 'Une carte par trou ; les autres trous restent visibles.',
  },
  {
    valeur: 'tout_masquer',
    libelle: 'Tout masquer',
    description: 'Une seule carte : tous les trous sont masqués, il faut tous les donner.',
  },
];

function modesMasquage(objet: 'zone' | 'case'): OptionListe<ModeMasquage>[] {
  const un = objet === 'zone' ? 'Une zone par carte' : 'Une case par carte';
  const nom = objet === 'zone' ? 'zone' : 'case grisée';
  const noms = objet === 'zone' ? 'zones' : 'cases grisées';
  const demandee = objet === 'zone' ? 'la zone demandée' : 'la case demandée';
  return [
    {
      valeur: 'un_par_carte',
      libelle: un,
      description: `Une carte par ${nom} ; seule ${demandee} est masquée.`,
    },
    {
      valeur: 'tout_masquer_tout_demander',
      libelle: 'Tout masquer, tout demander',
      description: `Une seule carte : toutes les ${noms} sont masquées, il faut toutes les donner.`,
    },
    {
      valeur: 'tout_masquer_une_demandee',
      libelle: 'Tout masquer, une seule demandée',
      description: `Une carte par ${nom} ; toutes sont masquées, ${demandee} est mise en évidence.`,
    },
  ];
}

export interface FormulaireCarteProps {
  brouillon: Brouillon;
  onChange: (brouillon: Brouillon) => void;
  erreurs?: ErreursCarte;
  desactive?: boolean;
  /** Carte existante : clés de ses cartes filles (avertit si certaines vont disparaître). */
  clesAvant?: string[];
  /** Le type ne change pas (modification pendant la séance). */
  typeFixe?: boolean;
  onEnregistrer?: () => void;
  onAnnuler?: () => void;
  envoyerImage: (image: Blob, extension: string) => Promise<string>;
  resoudreImage: ResoudreImage;
  couleursPersonnelles: string[];
  onAjouterCouleur: (couleur: string) => void;
  onErreur: (message: string) => void;
  barreCollee?: boolean;
}

export const FormulaireCarte = forwardRef<EditeurPoignee, FormulaireCarteProps>(
  function FormulaireCarte(
    {
      brouillon,
      onChange,
      erreurs = {},
      desactive = false,
      clesAvant,
      typeFixe = false,
      onEnregistrer,
      onAnnuler,
      envoyerImage,
      resoudreImage,
      couleursPersonnelles,
      onAjouterCouleur,
      onErreur,
      barreCollee,
    },
    ref,
  ) {
    const editeur = useRef<EditeurPoignee>(null);
    useImperativeHandle(ref, () => ({ focus: (nom) => editeur.current?.focus(nom) }));
    const brouillonCourant = useRef(brouillon);
    brouillonCourant.current = brouillon;
    const modifier = useCallback(
      (modification: (b: Brouillon) => Brouillon) =>
        onChange(modification(brouillonCourant.current)),
      [onChange],
    );

    const { type } = brouillon;
    const nombre = cartesGenerees(brouillon);
    const difference = clesAvant
      ? differenceCartesFilles(
          clesAvant,
          clesCartesFilles(normaliserCarteMere(carteDuBrouillon(brouillon))),
        )
      : null;
    const supprimees = difference?.aSupprimer.length ?? 0;
    const bilan =
      clesAvant && supprimees > 0 && nombre < clesAvant.length
        ? `${pluriel(nombre, 'carte générée', 'cartes générées')} au lieu de ${clesAvant.length}`
        : pluriel(nombre, 'carte générée', 'cartes générées');

    const ligneMode = (() => {
      switch (type) {
        case 'basique':
          return (
            <CaseACocher
              libelle="Inverse facultative"
              description="Crée aussi la carte verso → recto."
              coche={brouillon.modes.basique === 'avec_inverse'}
              desactivee={desactive}
              onChange={(coche) =>
                modifier((b) => ({
                  ...b,
                  modes: { ...b.modes, basique: coche ? 'avec_inverse' : 'sans_inverse' },
                }))
              }
            />
          );
        case 'trous':
          return (
            <ListeDeroulante
              className="fc-choix-du-type-de-carte__mode"
              etiquette={`Mode : ${MODES_TROUS.find((m) => m.valeur === brouillon.modes.trous)?.libelle ?? ''}`}
              options={MODES_TROUS}
              valeur={brouillon.modes.trous}
              desactive={desactive}
              onChoisir={(mode) => modifier((b) => ({ ...b, modes: { ...b.modes, trous: mode } }))}
            />
          );
        case 'image':
        case 'tableau': {
          const options = modesMasquage(type === 'image' ? 'zone' : 'case');
          return (
            <ListeDeroulante
              className="fc-choix-du-type-de-carte__mode"
              etiquette={`Mode : ${options.find((m) => m.valeur === brouillon.modes[type])?.libelle ?? ''}`}
              options={options}
              valeur={brouillon.modes[type]}
              desactive={desactive}
              onChoisir={(mode) => modifier((b) => ({ ...b, modes: { ...b.modes, [type]: mode } }))}
            />
          );
        }
      }
    })();

    const valeurEditeur =
      type === 'basique'
        ? { recto: brouillon.basique.recto, verso: brouillon.basique.verso }
        : { texte: brouillon.trous.texte };

    return (
      <>
        <div className="fc-choix-du-type-de-carte">
          {!typeFixe && (
            <div className="fc-choix-du-type-de-carte__ligne">
              <CommandeSegmentee
                etiquette="Type de carte"
                options={TYPES}
                valeur={type}
                desactivee={desactive}
                onChoisir={(t) => modifier((b) => ({ ...b, type: t }))}
              />
            </div>
          )}
          <div className="fc-choix-du-type-de-carte__ligne">
            {ligneMode}
            <p className="fc-choix-du-type-de-carte__bilan" aria-live="polite">
              <Icone nom="layers" />
              {bilan}
            </p>
          </div>
          {supprimees > 0 && (
            <MessageDeChamp id="formulaire-avertissement" ton="avertissement">
              {supprimees === 1
                ? 'Une carte fille et son suivi seront supprimés à l’enregistrement.'
                : `${pluriel(supprimees, 'carte fille', 'cartes filles')} et leur suivi seront supprimés à l’enregistrement.`}
            </MessageDeChamp>
          )}
        </div>
        <div className="fc-ecran-editeur__redaction">
          {type === 'basique' || type === 'trous' ? (
            <Editeur
              ref={editeur}
              type={type}
              champs={
                type === 'basique'
                  ? [
                      {
                        nom: 'recto',
                        libelle: 'Recto',
                        exemple:
                          'La question, par exemple « Quel nerf innerve le muscle deltoïde ? »',
                        lignes: 4,
                      },
                      { nom: 'verso', libelle: 'Verso', exemple: 'La réponse', lignes: 4 },
                    ]
                  : [
                      {
                        nom: 'texte',
                        libelle: 'Texte',
                        exemple: 'Le délai de prescription de droit commun est de {{5 ans}}.',
                        lignes: 6,
                      },
                    ]
              }
              valeur={valeurEditeur}
              onChange={(nom, markdown) =>
                modifier((b) =>
                  nom === 'texte'
                    ? { ...b, trous: { texte: markdown } }
                    : { ...b, basique: { ...b.basique, [nom]: markdown } },
                )
              }
              erreurs={erreurs}
              desactive={desactive}
              {...(onEnregistrer ? { onEnregistrer } : {})}
              {...(onAnnuler ? { onAnnuler } : {})}
              envoyerImage={envoyerImage}
              resoudreImage={resoudreImage}
              couleursPersonnelles={couleursPersonnelles}
              onAjouterCouleur={onAjouterCouleur}
              onErreur={onErreur}
              barreCollee={barreCollee ?? false}
            />
          ) : type === 'image' ? (
            <EditeurDImage
              contenu={brouillon.image}
              onChange={(image) => modifier((b) => ({ ...b, image }))}
              cartesGenerees={nombre}
              envoyerImage={envoyerImage}
              resoudreImage={resoudreImage}
              onErreur={onErreur}
              desactive={desactive}
              {...(erreurs.masquage ? { erreur: erreurs.masquage } : {})}
            />
          ) : (
            <EditeurDeTableau
              contenu={brouillon.tableau}
              onChange={(tableau) => modifier((b) => ({ ...b, tableau }))}
              cartesGenerees={nombre}
              desactive={desactive}
              {...(erreurs.masquage ? { erreur: erreurs.masquage } : {})}
            />
          )}
        </div>
      </>
    );
  },
);
